import { validateSheet, type SheetDescription } from "odyssey-shared";
import type { LayerPlace, OwnLook, StripOrder } from "./character-layers.ts";

/**
 * The layered trial look (P1.9-T88): the page's own character drawn from the Mana Seed character base's layers in
 * place of the player's own composed look, only on a page a development server serves on this machine or local
 * network (`trial-gate.ts`). It is drawing on this page and nothing else: the map server, the protocol and every
 * other player's page know nothing of it, so everyone else sees the look the server holds, and this page draws every
 * other player by that look.
 *
 * The look is asked for in the address:
 * `?look=mana-seed&skin=1&outfit=fstr1&hair=bob3&hat=none&weapon=1&shield=1`. Each choice names a sheet the
 * converter wrote (`bun run trial:mana-seed`, `tools/mana-seed-trial`), whose ids are `<slot>-<code>-<variant>`:
 * a bare number is that colour of the slot's first item (`skin=3`, `weapon=2` the sword), letters and a number the item
 * whose code starts with the letters in that colour (`fstr1`, `bob3`, `ax2`), `<code>-<variant>` is exact (`sh02-3`),
 * and `none` leaves the slot empty. The body cannot be left out. `cloak=` and `face=` add a cloak and a face item,
 * none by default. An item whose code ends in a digit and is not its slot's first of that name is asked for exactly
 * (`hair=bob2-3`, `shield=sh02-1`).
 *
 * Many items have art for walking only: every outfit but the forester's and the farmer's pants, every hairstyle but
 * `bob1` and `dap1`, most hats, the cloaks, the goggles and the goblin and demon skins. A look wearing one still loads
 * and walks; it plays only the strips all its layers share (`sharedStrips`), so its swings, hurt, dash and death are
 * drawn by its motion and its weapon stays as it is carried.
 */

/**
 * The only trial look there is.
 */
export const TRIAL_LOOK = "mana-seed";

/**
 * Where the converter writes the trial manifest, under the asset base. It is outside `assets/characters`, which the
 * publisher uploads.
 */
export const TRIAL_MANIFEST = "trial/characters/current.json";

/**
 * The slots of a look, bottom first, with the address parameter that chooses each, the letters a bare number stands
 * for (the slot's first item: `weapon=2` is `sw2`), what the slot wears when the address does not say, and the rank
 * its layer stacks at. The weapon's and the shield's ranks come frame by frame from the manifest's order tables and
 * fall to these, in front of the character, for a frame the tables lack.
 */
export const TRIAL_SLOTS: readonly { readonly slot: string; readonly param: string; readonly letters: string; readonly fallback: string; readonly rank: number }[] = [
  { slot: "body", param: "skin", letters: "humn", fallback: "1", rank: 0 },
  { slot: "outfit", param: "outfit", letters: "fstr", fallback: "1", rank: 0.2 },
  { slot: "cloak", param: "cloak", letters: "lnpl", fallback: "none", rank: 0.3 },
  { slot: "face", param: "face", letters: "gogl", fallback: "none", rank: 0.35 },
  { slot: "hair", param: "hair", letters: "bob", fallback: "1", rank: 0.4 },
  { slot: "hat", param: "hat", letters: "pfht", fallback: "none", rank: 0.6 },
  { slot: "weapon", param: "weapon", letters: "sw", fallback: "1", rank: 3 },
  { slot: "shield", param: "shield", letters: "sh", fallback: "1", rank: 2 },
];

/**
 * The slot holding the part of a cloak that hangs behind the body, and the rank it stacks at: under the body, over a
 * tool that is behind the character. It is never chosen: a cloak brings the sheet of its own item and colour when the
 * manifest has one.
 */
const UNDER = { slot: "under", rank: -0.5 };

const NONE = "none";
const NUMBER = /^[0-9]+$/;
const EXACT = /^([a-z0-9]+)-([0-9]+)$/;
const SHORT = /^([a-z]+)([0-9]+)$/;

/**
 * What the address asks each slot to wear, by slot.
 */
export type TrialLookRequest = Readonly<Record<string, string>>;

/**
 * The trial manifest: every sheet by id, how far each sheet's standing art reaches above its pivot, and the frame by
 * frame ranks of the slots that have them.
 */
export interface TrialManifest {
  readonly characters: Readonly<Record<string, SheetDescription>>;
  readonly heads: Readonly<Record<string, number>>;
  readonly order: Readonly<Record<string, StripOrder>>;
}

/**
 * Reads the look the address asks for.
 *
 * @param params The page's query.
 * @returns Each slot's choice, or null when `?look=` names no trial look this page knows.
 */
export function trialLookRequest(params: URLSearchParams): TrialLookRequest | null {
  if (params.get("look") !== TRIAL_LOOK) {
    return null;
  }
  const request: Record<string, string> = {};
  for (const { slot, param, fallback } of TRIAL_SLOTS) {
    const asked = params.get(param);
    request[slot] = asked === null || asked === "" ? fallback : asked.toLowerCase();
  }
  return request;
}

/**
 * Narrows to a plain object.
 *
 * @param value The value.
 * @param where Its name, for the error.
 * @returns The object.
 * @throws Error when it is not one.
 */
function asObject(value: unknown, where: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`trial manifest ${where} must be an object`);
  }
  return value as Record<string, unknown>;
}

/**
 * Parses one slot's order table.
 *
 * @param value The parsed table.
 * @param where Its name, for the error.
 * @returns The table.
 * @throws Error when a direction's ranks are not a list of numbers.
 */
function parseOrder(value: unknown, where: string): StripOrder {
  const order: Record<string, Record<string, readonly number[]>> = {};
  for (const [anim, directions] of Object.entries(asObject(value, where))) {
    const byDirection: Record<string, readonly number[]> = {};
    for (const [direction, ranks] of Object.entries(asObject(directions, `${where}.${anim}`))) {
      if (!Array.isArray(ranks) || !ranks.every((rank) => typeof rank === "number" && Number.isFinite(rank))) {
        throw new Error(`trial manifest ${where}.${anim}.${direction} must be a list of numbers`);
      }
      byDirection[direction] = ranks as number[];
    }
    order[anim] = byDirection;
  }
  return order;
}

/**
 * Parses and validates a trial manifest.
 *
 * @param value Parsed JSON.
 * @returns The manifest.
 * @throws Error naming what is malformed.
 */
export function parseTrialManifest(value: unknown): TrialManifest {
  const manifest = asObject(value, "root");
  const characters: Record<string, SheetDescription> = {};
  for (const [id, sheet] of Object.entries(asObject(manifest["characters"], "characters"))) {
    characters[id] = validateSheet(sheet);
  }
  const heads: Record<string, number> = {};
  for (const [id, head] of Object.entries(asObject(manifest["heads"], "heads"))) {
    if (typeof head !== "number" || !Number.isFinite(head) || head < 0) {
      throw new Error(`trial manifest heads.${id} must be a height in pixels`);
    }
    heads[id] = head;
  }
  const order: Record<string, StripOrder> = {};
  for (const [slot, table] of Object.entries(asObject(manifest["order"], "order"))) {
    order[slot] = parseOrder(table, `order.${slot}`);
  }
  return { characters, heads, order };
}

/**
 * The sheet a slot's choice names.
 *
 * @param slot The slot.
 * @param letters The letters a bare number stands for in this slot; empty when a bare number names nothing.
 * @param choice The choice: a number, letters and a number, or `<code>-<variant>`.
 * @param ids The manifest's sheet ids, sorted.
 * @returns The id.
 * @throws Error naming the slot, the choice and the slot's items when nothing matches.
 */
export function chosenSheet(slot: string, letters: string, choice: string, ids: readonly string[]): string {
  const prefix = `${slot}-`;
  const candidates = ids.filter((id) => id.startsWith(prefix));
  const exact = EXACT.exec(choice);
  const short = SHORT.exec(NUMBER.test(choice) ? `${letters}${choice}` : choice);
  let found: string | undefined;
  if (exact !== null) {
    found = candidates.find((id) => id === `${prefix}${exact[1]}-${Number(exact[2])}`);
  } else if (short !== null) {
    found = candidates.find((id) => id.startsWith(`${prefix}${short[1]}`) && id.endsWith(`-${Number(short[2])}`));
  }
  if (found === undefined) {
    const items = [...new Set(candidates.map((id) => id.slice(prefix.length, id.lastIndexOf("-"))))];
    throw new Error(`the trial look has no ${slot} "${choice}"; the ${slot} items are ${items.length === 0 ? "none" : items.join(", ")}`);
  }
  return found;
}

/**
 * Resolves a request against a manifest: the sheets to stack, where each stacks and the look's head height, the
 * tallest of its layers' standing art.
 *
 * @param request Each slot's choice.
 * @param manifest The trial manifest.
 * @returns The look.
 * @throws Error when a choice names no sheet, or the body is left out.
 */
export function resolveTrialLook(request: TrialLookRequest, manifest: TrialManifest): OwnLook {
  const ids = Object.keys(manifest.characters).sort();
  const layers: string[] = [];
  const places: LayerPlace[] = [];
  let head = 0;
  for (const { slot, letters, rank } of TRIAL_SLOTS) {
    const choice = request[slot] ?? NONE;
    if (choice === NONE) {
      if (slot === "body") {
        throw new Error("the trial look needs a body: skin cannot be none");
      }
      continue;
    }
    const id = chosenSheet(slot, letters, choice, ids);
    layers.push(id);
    places.push({ rank, order: manifest.order[slot] ?? null });
    const under = `${UNDER.slot}${id.slice(slot.length)}`;
    if (slot === "cloak" && manifest.characters[under] !== undefined) {
      layers.push(under);
      places.push({ rank: UNDER.rank, order: null });
    }
    head = Math.max(head, manifest.heads[id] ?? 0);
  }
  return { layers, places, head };
}

/**
 * Loads the trial look for the world boot: fetches the manifest and resolves the request.
 *
 * @param base The asset base the page loads from.
 * @param request Each slot's choice.
 * @param fetcher What fetches the manifest; the page's `fetch` unless a test hands in another.
 * @returns The chosen sheets by id and the look.
 * @throws Error when the manifest does not arrive (the converter has not run here), is malformed, or lacks a choice.
 */
export async function loadTrialLook(base: string, request: TrialLookRequest, fetcher: (url: string) => Promise<Response> = (url) => fetch(url)): Promise<{ readonly sheets: Readonly<Record<string, SheetDescription>>; readonly look: OwnLook }> {
  const url = `${base}/${TRIAL_MANIFEST}`;
  const response = await fetcher(url);
  if (!response.ok) {
    throw new Error(`${url} responded ${response.status}; run \`bun run trial:mana-seed\` to convert the pack`);
  }
  const manifest = parseTrialManifest(await response.json());
  const look = resolveTrialLook(request, manifest);
  const sheets: Record<string, SheetDescription> = {};
  for (const id of look.layers) {
    sheets[id] = manifest.characters[id] as SheetDescription;
  }
  return { sheets, look };
}
